"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import "./knowledge.css";

// Reveals an element with a fade + gentle upward drift the first time it
// scrolls into view. No left/right movement — only opacity + translateY.
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

/* href = card ka link. Jahan page nahi hai wahan niche note likha hai. */
const categories = [
  {
    icon: "▣",
    title: "Enterprise AI",
    text: "Concepts, use cases and implementation.",
    href: "/whatwedo/Ai-and-Intelligence/enterprise-ai",
  },
  {
    icon: "◉",
    title: "SAP & BDC",
    text: "Data, integration and analytics.",
    href: "/datasap",
  },
  {
    icon: "♧",
    title: "Business Problems",
    text: "52 real business problems.",
    href: "/52problem",
  },
  {
    icon: "↗",
    title: "Industry Knowledge",
    text: "Domain insights and playbooks.",
    href: "/industries",
  },
  {
    icon: "⚒",
    title: "Tools & Templates",
    text: "Frameworks, templates and accelerators.",
    href: "/insights/playbooks-downloads",
  },
  {
    icon: "◇",
    title: "Learning & Growth",
    text: "For professionals, students and teams.",
    href: "#learning", // same page ke Learning section par scroll
  },
];

const problems = [
  {
    image: "/assets/images/Demand.jpg",
    title: "Demand Forecasting Inaccuracy",
    text: "How to improve demand forecasting using SAP, data and AI.",
    date: "12 Sep 2026",
    read: "5 min read",
    href: "/knowledge/supply-chain",
  },
  {
    image: "/assets/images/Rising.jpg",
    title: "Rising Procurement Costs",
    text: "Use spend analytics to drive savings and reduce risk.",
    date: "09 Sep 2026",
    read: "6 min read",
    href: "/knowledge/vendor-spend",
  },
  {
    image: "/assets/images/low-sales.jpg",
    title: "Low Sales Productivity",
    text: "How to empower sales teams with data, AI and better insights.",
    date: "02 Sep 2026",
    read: "5 min read",
    href: "/knowledge/customer&revenue",
  },
  {
    image: "/assets/images/Demand.jpg",
    title: "Placeholder Title",
    text: "Placeholder description text.",
    date: "01 Sep 2026",
    read: "5 min read",
    href: "/52problem",
  },
];

/* Learning cards: abhi inke liye koi page nahi hai, isliye plain card (click nahi hota).
   Page aane par `href` field add kar do, card apne aap clickable ho jayega. */
const learning: {
  image: string;
  title: string;
  text: string;
  level: string;
  modules: string;
  href?: string;
}[] = [
  {
    image: "/assets/images/Business.jpg",
    title: "SAP for Business Professionals",
    text: "Understand SAP from a business perspective.",
    level: "Beginner",
    modules: "6 Modules",
  },
  {
    image: "/assets/images/Enterprise.jpg",
    title: "AI for the Enterprise",
    text: "Concepts, tools and real-world applications.",
    level: "Intermediate",
    modules: "8 Modules",
  },
  {
    image: "/assets/images/student.jpg",
    title: "Student & Teacher Programme",
    text: "How AI is changing learning and work.",
    level: "Foundation",
    modules: "6 Modules",
  },
  {
    image: "/assets/images/Business.jpg",
    title: "Placeholder Title",
    text: "Placeholder description text.",
    level: "Beginner",
    modules: "6 Modules",
  },
];

const RESOURCES_HREF = "/insights/playbooks-downloads";

const resources = [
  {
    icon: "▤",
    title: "Business Case Template",
    text: "Build a compelling business case.",
    href: RESOURCES_HREF,
  },
  {
    icon: "☑",
    title: "Data Readiness Checklist",
    text: "Assess your data environment.",
    href: RESOURCES_HREF,
  },
  {
    icon: "▦",
    title: "AI Use Case Canvas",
    text: "Design and evaluate AI use cases.",
    href: RESOURCES_HREF,
  },
  {
    icon: "⌁",
    title: "S/4HANA Readiness Assessment",
    text: "Check your migration readiness.",
    href: RESOURCES_HREF,
  },
];

const research = [
  {
    image: "/assets/images/business-value.jpg",
    title: "The Business Value of AI in FMCG",
    text: "From pilots to measurable impact.",
    date: "28 Aug 2026",
    read: "8 min read",
    href: "/industries/fmcg-retail",
  },
  {
    image: "/assets/images/airline-seat.jpg",
    title: "What Is One Airline Seat Really Worth?",
    text: "A story of networks, pricing and AI.",
    date: "08 Aug 2026",
    read: "6 min read",
    href: "/industries/airlines-airports",
  },
  {
    image: "/assets/images/business-value.jpg",
    title: "Placeholder Title",
    text: "Placeholder description text.",
    date: "01 Aug 2026",
    read: "5 min read",
    href: "/insights",
  },
];

export default function Knowledge() {
  const categories$ = useReveal<HTMLDivElement>();
  const framework$ = useReveal<HTMLElement>();
  const problems$ = useReveal<HTMLDivElement>();
  const learning$ = useReveal<HTMLDivElement>();
  const resources$ = useReveal<HTMLDivElement>();
  const research$ = useReveal<HTMLDivElement>();
  const download$ = useReveal<HTMLDivElement>();

  return (
    <div className="know-knowledge-page">


      {/* HERO */}
      <section className="know-knowledge-hero">

        <div className="know-hero-content">

          <h1 className="know-loadIn" style={{ animationDelay: "0.15s" }}>
            Real Knowledge
            <br />
            for <span>Real Progress.</span>
          </h1>

          <p className="know-loadIn" style={{ animationDelay: "0.28s" }}>
            Frameworks, business problems, use cases and practical{" "}
            <br />
            guidance at the intersection of business, SAP and AI.
          </p>

        </div>


      </section>


      {/* CATEGORY CARDS */}
      <section className="know-knowledge-container know-category-section">

        <div
          className={`know-category-grid know-reveal${categories$.visible ? " know-inView" : ""}`}
          ref={categories$.ref}
        >

          {categories.map((item, i) => (
            <Link
              className="know-category-card"
              key={item.title}
              href={item.href}
              style={{ transitionDelay: categories$.visible ? `${i * 0.06}s` : "0s" }}
            >

              <div className="know-category-icon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.text}</p>

            </Link>
          ))}

        </div>

      </section>


      {/* FEATURED FRAMEWORK */}
      <section
        className={`know-knowledge-container know-framework-section know-reveal${framework$.visible ? " know-inView" : ""}`}
        ref={framework$.ref}
      >

        <div className="know-framework-text">

          <div className="know-section-eyebrow">
            FEATURED FRAMEWORK
          </div>

          <h2>
            The Enterprise AI Map
          </h2>

          <p>
            A practical way to connect Business, Data,
            AI and Outcomes.
          </p>

          <Link className="know-primary-button" href="/whatwedo/Ai-and-Intelligence/enterprise-ai">
            Explore the Framework
            <span>→</span>
          </Link>

        </div>


        <div className="know-ai-map">

          <img
            src="/assets/images/enterprise-ai-map.jpg"
            alt="Enterprise AI Map — AI & Agents, Applications & Processes, Data & Semantics, Core Enterprise Systems, Business Context & Value"
            className="know-ai-map-image"
          />

        </div>

      </section>


      {/* PROBLEMS */}
      <section className="know-knowledge-container know-content-section">

        <div className="know-section-heading">

          <div>

            <div className="know-section-eyebrow">
              EXPLORE BY BUSINESS PROBLEM
            </div>

            <h2>
              52 Problems. Real Solutions.
            </h2>

            <p>
              One real business problem every week — with practical
              insights, use cases and expert perspectives.
            </p>

          </div>

          <Link href="/52problem" className="know-view-link">
            View All 52 Problems →
          </Link>

        </div>


        <div
          className={`know-article-grid know-reveal${problems$.visible ? " know-inView" : ""}`}
          ref={problems$.ref}
        >

          {problems.map((item, i) => (
            <Link
              className="know-article-card"
              key={item.title + i}
              href={item.href}
              style={{ transitionDelay: problems$.visible ? `${i * 0.07}s` : "0s" }}
            >

              <img src={item.image} alt={item.title} />

              <div className="know-article-content">

                <h3>{item.title}</h3>

                <p>{item.text}</p>

                <div className="know-article-footer">

                  <span>{item.read}</span>

                  <span>|</span>

                  <span>{item.date}</span>

                  <div className="know-card-arrow" aria-hidden="true">→</div>

                </div>

              </div>

            </Link>
          ))}

        </div>

      </section>


      {/* LEARNING */}
      <section className="know-knowledge-container know-content-section" id="learning">

        <div className="know-section-heading">

          <div>

            <div className="know-section-eyebrow">
              LEARNING &amp; DEVELOPMENT
            </div>

            <h2>
              Build Future-Ready Skills.
            </h2>

            <p>
              Curated learning paths for professionals,
              teams and students.
            </p>

          </div>

          {/* TODO: Learning ka page aane par yahan href badlo */}
          <Link href="#learning" className="know-view-link">
            View All Learning →
          </Link>

        </div>


        <div
          className={`know-article-grid know-reveal${learning$.visible ? " know-inView" : ""}`}
          ref={learning$.ref}
        >

          {learning.map((item, i) => {
            const inner = (
              <>
                <img src={item.image} alt={item.title} />

                <div className="know-article-content">

                  <h3>{item.title}</h3>

                  <p>{item.text}</p>

                  <div className="know-learning-footer">

                    <span>{item.level}</span>

                    <small>{item.modules}</small>

                    <div className="know-card-arrow" aria-hidden="true">→</div>

                  </div>

                </div>
              </>
            );
            const delay = { transitionDelay: learning$.visible ? `${i * 0.07}s` : "0s" };
            /* href na ho to card plain article rahega */
            return item.href ? (
              <Link className="know-article-card" key={item.title + i} href={item.href} style={delay}>
                {inner}
              </Link>
            ) : (
              <article className="know-article-card" key={item.title + i} style={delay}>
                {inner}
              </article>
            );
          })}

        </div>

      </section>


      {/* RESOURCES */}
      <section className="know-knowledge-container know-content-section">

        <div className="know-section-heading">

          <div>

            <div className="know-section-eyebrow">
              TOOLS &amp; TEMPLATES
            </div>

            <h2>
              Accelerate with Practical Resources.
            </h2>

          </div>

          <Link href={RESOURCES_HREF} className="know-view-link">
            View All Resources →
          </Link>

        </div>


        <div
          className={`know-resources-grid know-reveal${resources$.visible ? " know-inView" : ""}`}
          ref={resources$.ref}
        >

          {resources.map((item, i) => (
            <div
              className="know-resource-card"
              key={item.title}
              style={{ transitionDelay: resources$.visible ? `${i * 0.06}s` : "0s" }}
            >

              <div className="know-resource-icon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.text}</p>

              <Link href={item.href}>
                Download →
              </Link>

            </div>
          ))}

        </div>

      </section>


      {/* RESEARCH */}
      <section className="know-knowledge-container know-content-section">

        <div className="know-section-heading">

          <div>

            <div className="know-section-eyebrow">
              RESEARCH &amp; PERSPECTIVES
            </div>

            <h2>
              Ideas That Move You Forward.
            </h2>

          </div>

          <Link href="/insights" className="know-view-link">
            View All →
          </Link>

        </div>


        <div
          className={`know-research-grid know-reveal${research$.visible ? " know-inView" : ""}`}
          ref={research$.ref}
        >

          {research.map((item, i) => (
            <Link
              className="know-research-card"
              key={item.title + i}
              href={item.href}
              style={{ transitionDelay: research$.visible ? `${i * 0.07}s` : "0s" }}
            >

              <img src={item.image} alt={item.title} />

              <div className="know-research-content">

                <h3>{item.title}</h3>

                <p>{item.text}</p>

                <div className="know-article-footer">

                  <span>{item.read}</span>

                  <span>|</span>

                  <span>{item.date}</span>

                  <div className="know-card-arrow" aria-hidden="true">→</div>

                </div>

              </div>

            </Link>
          ))}

        </div>

      </section>


      {/* DOWNLOAD BANNER (CTA) */}
      <section className="know-knowledge-container">

        <div
          className={`know-download-banner know-reveal${download$.visible ? " know-inView" : ""}`}
          ref={download$.ref}
        >

          <div className="know-download-text">

            <h2>
              The 10-Chapter Guide to Enterprise AI
            </h2>

            <p>
              A practical playbook for business leaders,
              professionals and students.
            </p>

          </div>


          <div className="know-book-stack">

            <img
              src="/assets/images/enterprise-ai-book-stack.jpg"
              alt="Stack of books — The 10-Chapter Guide to Enterprise AI"
              className="know-book-stack-image"
            />

          </div>


          <Link className="fp-buttons fp-btn-primary know-download-button" href={RESOURCES_HREF}>
            Download the Book <span aria-hidden="true">→</span>
          </Link>


        </div>

      </section>


    </div>
  );
}